import React, { useState, useEffect } from 'react';
import { myAxios } from '../services/Helper';
import './ManageBookings.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faCalendarAlt, faClock, faUser, faEnvelope, faPhone, faMoneyBillWave } from '@fortawesome/free-solid-svg-icons';

const ManageBookings = () => {
    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filter, setFilter] = useState('ALL');

    useEffect(() => {
        fetchBookings();
    }, []);

    const fetchBookings = async () => {
        try {
            const response = await myAxios.get('/api/booking/booking');
            setBookings(response.data);
            setLoading(false);
        } catch (err) {
            setError(err.message);
            setLoading(false);
        }
    };

    const updateBookingStatus = async (bookingId, newStatus) => {
        try {
            // Optimistically update UI
            setBookings(prevBookings =>
                prevBookings.map(booking =>
                    booking.bookingId === bookingId
                        ? { ...booking, bookingStatus: newStatus }
                        : booking
                )
            );

            // Call your correct API
            await myAxios.put('/api/booking/changeStatus', {
                bookingId: bookingId,
                status: newStatus
            });

            // Optionally refresh from server after update
            // await fetchBookings();
        } catch (err) {
            setError(err.message);
        }
    };

    const handleDeleteBooking = async (bookingId) => {
        if (window.confirm('Are you sure you want to delete this booking?')) {
            try {
                await myAxios.delete(`/api/booking/booking/${bookingId}`);
                fetchBookings();
            } catch (err) {
                setError(err.message);
            }
        }
    };

    const filteredBookings = filter === 'ALL'
        ? bookings
        : bookings.filter(booking => booking.bookingStatus === filter);

    if (loading) return <div className="loading-container"><div className="loading">Loading bookings...</div></div>;
    if (error) return <div className="error-container"><div className="error">Error: {error}</div></div>;

    return (
        <div className="bookings-container">
            <div className="bookings-header">
                <h2>Manage Bookings</h2>
                <div className="filter-controls">
                    <select
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                        className="filter-select"
                    >
                        <option value="ALL">All Bookings</option>
                        <option value="PENDING">Pending</option>
                        <option value="CONFIRMED">Confirmed</option>
                        <option value="CANCELLED">Cancelled</option>
                        <option value="COMPLETED">Completed</option>
                    </select>
                </div>
            </div>

            <div className="bookings-grid">
                {filteredBookings.length === 0 ? (
                    <div className="no-data">No bookings found</div>
                ) : (
                    filteredBookings.map((booking) => (
                        <div key={booking.bookingId} className="booking-card">
                            <div className="booking-header">
                                <h3>Booking #{booking.bookingId}</h3>
                                <span className={`status-badge ${booking.bookingStatus.toLowerCase()}`}>
                                    {booking.bookingStatus}
                                </span>
                            </div>

                            <div className="booking-body">
                                <div className="booking-section customer-details">
                                    <h4>Customer Details</h4>
                                    <p>
                                        <FontAwesomeIcon icon={faUser} className="booking-icon" />
                                        <span>{booking.firstName} {booking.lastName}</span>
                                    </p>
                                    <p>
                                        <FontAwesomeIcon icon={faPhone} className="booking-icon" />
                                        <span>{booking.mobile}</span>
                                    </p>
                                    <p>
                                        <FontAwesomeIcon icon={faEnvelope} className="booking-icon" />
                                        <span>{booking.email}</span>
                                    </p>
                                </div>

                                <div className="booking-section event-details">
                                    <h4>Event Details</h4>
                                    <p><strong>Function:</strong> {booking.functionName}</p>

                                    <div className="sub-section">
                                        <p><strong>Sub-Functions:</strong></p>
                                        <ul className="item-list">
                                            {booking.subfunction.map(sub => (
                                                <li key={sub.id}>
                                                    {sub.subfunctionName} <span className="price">₹{sub.price}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>

                                    {booking.items && booking.items.length > 0 && (
                                        <div className="sub-section">
                                            <p><strong>Items:</strong></p>
                                            <ul className="item-list">
                                                {booking.items.map(item => (
                                                    <li key={item.id}>{item.name}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}

                                    <p>
                                        <FontAwesomeIcon icon={faCalendarAlt} className="booking-icon" />
                                        <span>{new Date(booking.bookingForDate).toLocaleDateString()}</span>
                                    </p>
                                    <p>
                                        <FontAwesomeIcon icon={faClock} className="booking-icon" />
                                        <span>{booking.startTime} - {booking.endTime}</span>
                                    </p>
                                </div>

                                <div className="booking-section payment-details">
                                    <h4>Payment Details</h4>
                                    <p>
                                        <FontAwesomeIcon icon={faMoneyBillWave} className="booking-icon" />
                                        <span className="total-amount">₹{booking.totalPaymentAmount.toLocaleString()}</span>
                                    </p>
                                    <p><strong>Mode:</strong> {booking.paymentMode}</p>
                                </div>
                            </div>

                            <div className="booking-footer">
                                <select
                                    value={booking.bookingStatus}
                                    onChange={(e) => updateBookingStatus(booking.bookingId, e.target.value)}
                                    className="status-select"
                                >
                                    <option value="PENDING">Pending</option>
                                    <option value="CONFIRMED">Confirmed</option>
                                    <option value="CANCELLED">Cancelled</option>
                                    <option value="COMPLETED">Completed</option>
                                </select>

                                <button
                                    className="delete-btn"
                                    onClick={() => handleDeleteBooking(booking.bookingId)}
                                >
                                    <FontAwesomeIcon icon={faTrash} /> Delete
                                </button>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

export default ManageBookings;
